import type { FC } from 'react';
import { StyleSheet, View, StatusBar as ReactNativeStatusBar, ScrollView } from 'react-native';
import ErrorView from 'src/presentation/components/organisms/ErrorView';
import ExerciseGridCard from 'src/presentation/components/organisms/ExerciseGridCard';
import ExerciseOfTheDayCard from 'src/presentation/components/organisms/ExerciseOfTheDayCard';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import BackgroundPattern from 'src/presentation/components/atoms/BackgroundPattern';
import SectionTitle from 'src/presentation/components/molecules/SectionTitle';
import Tag from 'src/presentation/components/molecules/Tag';
import TagRow from 'src/presentation/components/molecules/TagRow';
import { StatusBar } from 'expo-status-bar';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import { useContentStore } from 'src/domain/stores/useContentStore';
import { useInspirationStore } from 'src/domain/stores/useInspirationStore';
import Dimens from 'src/core/utils/Dimens';

const InspirationScreen: FC = () => {
	const tabBarInset = useTabBarInset();
	const { inspiration, inspirationLoading, inspirationErrorMessage } = useContentStore();
	const { selectedCategory, entranceKey, init, getInspiration, setSelectedCategory, replayEntrance } =
		useInspirationStore();

	const fetchData = () => getInspiration();

	useAppStateScreenListener({
		onInit: () => init(),
		onFocussed: () => {
			ReactNativeStatusBar.setBarStyle('dark-content');

			replayEntrance();
			fetchData();
		},
		onForegrounded: fetchData,
	});

	const exerciseOfTheDay = inspiration?.data.exercise_of_the_day ?? null;
	const selectedCategoryId = selectedCategory?.id ?? null;
	const cards = (inspiration?.data?.cards ?? []).filter(
		(card) =>
			selectedCategoryId === null || card.categories?.find((cardCategory) => cardCategory.id === selectedCategoryId),
	);

	return (
		<View style={styles.container}>
			<StatusBar style={'dark'} />
			<BackgroundPattern />
			{inspirationLoading ? (
				<LoadingIndicator style={styles.loadingIndicator} />
			) : inspirationErrorMessage ? (
				<ErrorView onPress={fetchData} />
			) : (
				<ScrollView contentContainerStyle={{ paddingBottom: tabBarInset }}>
					{inspiration && (
						<>
							<ExerciseOfTheDayCard content={exerciseOfTheDay} style={styles.exerciseCard} variant="dark" />
							<View style={styles.contentContainer}>
								<SectionTitle subtitle={inspiration.data.description} title={inspiration.data.title} />
								<TagRow>
									{inspiration.data.categories.map((category) => (
										<Tag
											key={category.id}
											label={category.name}
											selected={selectedCategoryId === category.id}
											onPress={() => setSelectedCategory(category)}
										/>
									))}
								</TagRow>
								<View key={`${selectedCategoryId}-${entranceKey}`} style={styles.grid}>
									{cards.map((item, index) => (
										<ExerciseGridCard content={item} index={index} key={item.id} />
									))}
								</View>
							</View>
						</>
					)}
				</ScrollView>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: Dimens.flex1,
	},
	loadingIndicator: {
		flex: Dimens.flex1,
	},
	contentContainer: {
		paddingVertical: Dimens.padding24,
		paddingHorizontal: Dimens.padding18,
		gap: Dimens.gap24,
	},
	exerciseCard: {
		marginHorizontal: Dimens.padding18,
		marginTop: Dimens.padding14,
	},
	grid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: Dimens.gap16,
	},
});

export default InspirationScreen;
